import React from "react";
import { Trash2 } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import Checkbox from "../ui/Checkbox";
import { getThreadedWasmSupport } from "../../physics/threadedWasmSupport";
import { disposeAll as disposeAssetCache } from "../../runtime/assetCache";
import { getAssetActions } from "../../stores/assetStore";
import { clearAllAssets } from "../../utils/idb";
import {
  isAssetPersistenceEnabled,
  setAssetPersistenceEnabled,
  subscribeAssetPersistencePreference,
} from "../../utils/assetStoragePreference";
import {
  isSentryErrorReportingEnabled,
  setSentryErrorReportingEnabled,
  subscribeSentryErrorReportingPreference,
} from "../../utils/sentryPreference";
import { logger } from "../../utils/logger";

const IS_DEV = import.meta.env.DEV;

// --- License text helpers ---

const BSD_3_BOILERPLATE = `Redistribution and use in source and binary forms, with or without
modification, are permitted provided that the following conditions are met:

1. Redistributions of source code must retain the above copyright notice, this
   list of conditions and the following disclaimer.

2. Redistributions in binary form must reproduce the above copyright notice,
   this list of conditions and the following disclaimer in the documentation
   and/or other materials provided with the distribution.

3. Neither the name of the copyright holder nor the names of its
   contributors may be used to endorse or promote products derived from
   this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.`;

const bsd3 = (copyright) =>
  `BSD 3-Clause License\n\n${copyright}\nAll rights reserved.\n\n${BSD_3_BOILERPLATE}`;

const MIT_BOILERPLATE = `Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.`;

const mit = (copyright) => `MIT License\n\n${copyright}\n\n${MIT_BOILERPLATE}`;

// --- License text constants ---

const SPOT_LICENSE = bsd3("Copyright (c) 2021, Clearpath Robotics Inc.");

const TROSSEN_LICENSE = bsd3("Copyright (c) 2023, Trossen Robotics");

const UR5E_LICENSE = `Copyright 2018 ROS Industrial Consortium

${BSD_3_BOILERPLATE}`;

const UNITREE_MENAGERIE_LICENSE = `BSD 3-Clause License

Copyright (c) 2016-2022 HangZhou YuShu TECHNOLOGY CO.,LTD. ("Unitree Robotics")  [Go2]
Copyright (c) 2016-2023 HangZhou YuShu TECHNOLOGY CO.,LTD. ("Unitree Robotics")  [G1]
All rights reserved.

${BSD_3_BOILERPLATE}`;

const UNITREE_MUJOCO_LICENSE = bsd3(
  `Copyright (c) 2016-2024 HangZhou YuShu TECHNOLOGY CO.,LTD. ("Unitree Robotics")`
);

const ARX_LICENSE = bsd3("Copyright (c) 2023-2025, ARXrobotics");

const ANYBOTICS_B_LICENSE = bsd3("Copyright 2019, ANYbotics AG.");

const ANYBOTICS_C_LICENSE = bsd3("Copyright 2020, ANYbotics AG.");

const ROBOTIQ_LICENSE = `Copyright (c) 2013, ROS-Industrial
All rights reserved.

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

  Redistributions of source code must retain the above copyright notice, this
  list of conditions and the following disclaimer.

  Redistributions in binary form must reproduce the above copyright notice, this
  list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR
ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON
ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.`;

const CASSIE_LICENSE = mit("Copyright (c) 2022 Agility Robotics");

const PIPER_LICENSE = mit("Copyright (c) 2024 RosenYin");

const UMI_LICENSE = mit("Copyright (c) 2023 Columbia Artificial Intelligence and Robotics Lab");

// --- UI components ---

const ABOUT_TAB_IDS = Object.freeze({
  BROWSER: "browser",
  LOCAL_STORAGE: "localStorage",
  PRIVACY: "privacy",
  LICENSES: "licenses",
});

const ABOUT_TABS = Object.freeze([
  { id: ABOUT_TAB_IDS.BROWSER, label: "Browser" },
  { id: ABOUT_TAB_IDS.PRIVACY, label: "Privacy" },
  { id: ABOUT_TAB_IDS.LICENSES, label: "Licenses" },
]);

const settingsSectionClass = "rounded-lg border border-gray-800/80 bg-gray-950/30";
const settingsRowClass = "flex min-w-0 items-center justify-between gap-3 px-3 py-2";

function SupportStatus({ ok, label }) {
  return (
    <span
      className={`rounded border px-2 py-0.5 text-xs ${
        ok
          ? "border-green-800/80 bg-green-950/50 text-green-200"
          : "border-amber-800/80 bg-amber-950/50 text-amber-200"
      }`}
    >
      {label}
    </span>
  );
}

const AboutTabStrip = React.memo(function AboutTabStrip({ activeTabId, onTabChange, tabs }) {
  return (
    <div className="px-3 pt-2">
      <div className="flex flex-wrap gap-0.5 pb-px" role="tablist" aria-label="About sections">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`shrink-0 rounded-t border px-2.5 py-1.5 text-xs transition-colors select-none ${
                isActive
                  ? "border-gray-600 border-b-gray-800 bg-gray-800 text-gray-100 -mb-px z-[1]"
                  : "border-transparent text-gray-300 hover:text-gray-100 hover:bg-gray-800/40"
              }`}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

const BrowserTabContent = React.memo(function BrowserTabContent() {
  const threadedWasmSupport = React.useMemo(() => getThreadedWasmSupport(), []);
  const hasSharedArrayBuffer = typeof globalThis.SharedArrayBuffer !== "undefined";
  const hasWebAssembly = typeof globalThis.WebAssembly !== "undefined";
  const isCrossOriginIsolated = globalThis.crossOriginIsolated === true;

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Browser Support</h3>
        <p>
          Critter supports the latest two major versions of Chrome, Edge, Firefox, and Safari for
          editing and viewing.
        </p>
        <p>
          Physics uses MuJoCo threaded WebAssembly and requires WebAssembly, SharedArrayBuffer, and
          cross-origin isolation. Safari physics is supported when this runtime check passes.
        </p>
      </section>

      <section className={settingsSectionClass}>
        <div className={settingsRowClass}>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-gray-100">Physics runtime</p>
            <p className="text-xs leading-5 text-gray-300">
              MuJoCo threaded WASM support in this browser session.
            </p>
          </div>
          <SupportStatus
            ok={threadedWasmSupport.ok}
            label={threadedWasmSupport.ok ? "Available" : "Unavailable"}
          />
        </div>
        {!threadedWasmSupport.ok && (
          <div className="border-t border-gray-800/80 px-3 py-2 text-xs leading-5 text-amber-200">
            {threadedWasmSupport.message}
          </div>
        )}
      </section>

      <section className={settingsSectionClass}>
        <div className={settingsRowClass}>
          <span className="text-sm text-gray-200">WebAssembly</span>
          <SupportStatus ok={hasWebAssembly} label={hasWebAssembly ? "Available" : "Missing"} />
        </div>
        <div className={`${settingsRowClass} border-t border-gray-800/80`}>
          <span className="text-sm text-gray-200">Cross-origin isolation</span>
          <SupportStatus
            ok={isCrossOriginIsolated}
            label={isCrossOriginIsolated ? "Enabled" : "Disabled"}
          />
        </div>
        <div className={`${settingsRowClass} border-t border-gray-800/80`}>
          <span className="text-sm text-gray-200">SharedArrayBuffer</span>
          <SupportStatus
            ok={hasSharedArrayBuffer}
            label={hasSharedArrayBuffer ? "Available" : "Missing"}
          />
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Feature Floors</h3>
        <p>
          Browser-trained model inference requires WebAssembly SIMD and is unavailable when
          unsupported. Local asset persistence uses IndexedDB and may be limited by private browsing
          or storage settings.
        </p>
      </section>
    </div>
  );
});

const LocalStorageTabContent = React.memo(function LocalStorageTabContent() {
  const [clearState, setClearState] = React.useState("idle");
  const [rememberAssets, setRememberAssets] = React.useState(() => isAssetPersistenceEnabled());
  const [errorReportingEnabled, setErrorReportingEnabledState] = React.useState(() =>
    isSentryErrorReportingEnabled()
  );

  React.useEffect(
    () =>
      subscribeAssetPersistencePreference((enabled) => {
        setRememberAssets(enabled);
      }),
    []
  );

  React.useEffect(
    () =>
      subscribeSentryErrorReportingPreference((enabled) => {
        setErrorReportingEnabledState(enabled);
      }),
    []
  );

  const handleClearStoredAssets = React.useCallback(async () => {
    const ok = window.confirm(
      "Delete locally stored mesh and texture files? This clears Critter's browser asset cache for this site."
    );
    if (!ok) return;

    setClearState("clearing");
    try {
      disposeAssetCache();
      await clearAllAssets();
      getAssetActions().clearAll();
      setClearState("cleared");
    } catch (error) {
      logger.warn("[AboutPanel] Failed to clear locally stored assets", error);
      setClearState("error");
    }
  }, []);

  const handleRememberAssetsChange = React.useCallback((event) => {
    const enabled = event.target.checked;
    setRememberAssets(enabled);
    setAssetPersistenceEnabled(enabled);
  }, []);

  const handleErrorReportingChange = React.useCallback((event) => {
    const enabled = event.target.checked;
    setErrorReportingEnabledState(enabled);
    setSentryErrorReportingEnabled(enabled);
  }, []);

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Browser Cache</h3>
        <p>
          Meshes and textures you upload may be stored in this browser so projects can continue to
          reference those files after reloads. Browser storage is shared by anyone using this same
          browser profile.
        </p>
      </section>

      <section className={settingsSectionClass}>
        <div className={settingsRowClass}>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-gray-100">Remember uploaded assets</p>
            <p className="text-xs leading-5 text-gray-300">
              Store new uploaded meshes and textures in this browser.
            </p>
          </div>
          <Checkbox
            checked={rememberAssets}
            onChange={handleRememberAssetsChange}
            aria-label="Remember uploaded assets in this browser"
            className="w-auto px-0 py-0"
          />
        </div>
        {!rememberAssets && (
          <div className="border-t border-gray-800/80 px-3 py-2 text-xs leading-5 text-amber-200">
            New uploads stay session-only. Reloading may require uploading those files again.
          </div>
        )}
      </section>

      <section className={settingsSectionClass}>
        <div className={settingsRowClass}>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-gray-100">Stored assets</p>
            <p className="text-xs leading-5 text-gray-300">
              Clear this site&apos;s local mesh and texture cache.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1.5 rounded border border-gray-700 px-2.5 py-1.5 text-xs text-gray-200 transition-colors hover:border-red-700 hover:bg-red-900/30 hover:text-red-100 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={clearState === "clearing"}
            onClick={handleClearStoredAssets}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            {clearState === "clearing" ? "Clearing..." : "Clear Stored Assets"}
          </button>
        </div>
        {clearState === "cleared" && (
          <div className="border-t border-gray-800/80 px-3 py-2 text-xs text-green-300">
            Stored assets cleared.
          </div>
        )}
        {clearState === "error" && (
          <div className="border-t border-gray-800/80 px-3 py-2 text-xs text-red-300">
            Could not clear stored assets.
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Error Reporting</h3>
      </section>

      <section className={settingsSectionClass}>
        <div className={settingsRowClass}>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium text-gray-100">Send technical error reports</p>
            <p className="text-xs leading-5 text-gray-300">
              Allow Sentry crash and browser error reports from this browser.
            </p>
          </div>
          <Checkbox
            checked={errorReportingEnabled}
            onChange={handleErrorReportingChange}
            aria-label="Send technical error reports"
            className="w-auto px-0 py-0"
          />
        </div>
        {!errorReportingEnabled && (
          <div className="border-t border-gray-800/80 px-3 py-2 text-xs leading-5 text-amber-200">
            Future reports are blocked now. Reload the app to prevent Sentry from initializing on
            startup.
          </div>
        )}
      </section>
    </div>
  );
});

const PrivacyTabContent = React.memo(function PrivacyTabContent() {
  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Privacy</h3>
        <p>
          Critter is designed to avoid collecting or storing user data. The app does not require an
          account, and project files are opened and edited in your browser.
        </p>
        <p>
          Your robot models, uploaded files, scenes, and project contents stay local in your
          browser. If cloud storage or server-backed project features are added later, they will be
          opt-in.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Error Reporting</h3>
        <p>
          Critter uses Sentry to collect technical error reports when the app crashes or encounters
          browser errors. These reports help identify and fix bugs before users need to report them
          manually.
        </p>
        <p>
          Error reports may include stack traces, browser and operating system information, page
          URL, and recent diagnostic console messages.
        </p>
        <p>
          Sentry is configured not to intentionally collect personal information, session replays,
          screen recordings, or performance traces. Diagnostic data is scrubbed before it is sent to
          remove query strings, local file paths, user context, and sensitive-looking fields.
        </p>
        <p>Technical error reporting can be changed in Privacy Settings.</p>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Forms and Updates</h3>
        <p>
          Get Updates and Send Feedback open www.critterengine.com in a new browser tab. Those
          website forms are outside the app workspace and do not receive your robot models, uploaded
          files, scenes, or project contents automatically.
        </p>
        <p>
          Do not include private project details in website forms unless you choose to share them.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Your Content</h3>
        <p>
          Avoid putting sensitive personal information in project names, file names, or model
          labels. Error reports are scrubbed before they are sent, but names and other authored text
          may still appear in diagnostic messages. You can turn off technical error reporting in
          Privacy Settings.
        </p>
      </section>
    </div>
  );
});

function SourceLink({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-blue-300 underline-offset-2 hover:underline"
    >
      {label}
    </a>
  );
}

function LicenseEntry({ title, sources, description, licenseText, apacheLicense, ccByLicense }) {
  return (
    <div className="space-y-2 border-t border-gray-700/60 pt-4 first:border-t-0 first:pt-0">
      <p className="font-medium text-gray-100">{title}</p>
      {sources.map((s) => (
        <p key={s.href}>
          Source: <SourceLink href={s.href} label={s.label} />
        </p>
      ))}
      {description && <p>{description}</p>}
      {licenseText && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-gray-700 bg-black/25 p-3 text-xs leading-5 text-gray-300">
          {licenseText}
        </pre>
      )}
      {apacheLicense && (
        <p>
          Licensed under the{" "}
          <SourceLink
            href="https://www.apache.org/licenses/LICENSE-2.0"
            label="Apache License, Version 2.0"
          />
          .
        </p>
      )}
      {ccByLicense && (
        <p>
          Licensed under{" "}
          <SourceLink
            href="https://creativecommons.org/licenses/by/4.0/"
            label="Creative Commons Attribution 4.0 International (CC BY 4.0)"
          />
          .
        </p>
      )}
    </div>
  );
}

const LicensesTabContent = React.memo(function LicensesTabContent() {
  return (
    <section className="space-y-0">
      <div className="space-y-2 pb-4">
        <h3 className="text-xs font-semibold uppercase text-gray-300">Critter Engine</h3>
        <p>Copyright (c) 2026 Critter Engine LLC. All rights reserved.</p>
        <p>
          Critter Engine and its source code are proprietary software. Third-party model assets and
          libraries may be governed by their own licenses below.
        </p>
      </div>

      <h3 className="border-t border-gray-700/60 pt-4 pb-4 text-xs font-semibold uppercase text-gray-300">
        Third-Party Licenses
      </h3>

      <LicenseEntry
        title="Franka Emika Panda, Shadow Hand, TetherIA Aero Hand Open"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/franka_emika_panda",
            label: "mujoco_menagerie/franka_emika_panda",
          },
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/shadow_hand",
            label: "mujoco_menagerie/shadow_hand",
          },
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/tetheria_aero_hand_open",
            label: "mujoco_menagerie/tetheria_aero_hand_open",
          },
        ]}
        description="Shadow Hand: Copyright 2022 Shadow Robot Company Ltd. These model assets are distributed under the Apache License 2.0."
        apacheLicense
      />

      <LicenseEntry
        title="Agility Cassie"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/agility_cassie",
            label: "mujoco_menagerie/agility_cassie",
          },
        ]}
        licenseText={CASSIE_LICENSE}
      />

      <LicenseEntry
        title="Agilex Piper"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/agilex_piper",
            label: "mujoco_menagerie/agilex_piper",
          },
        ]}
        licenseText={PIPER_LICENSE}
      />

      <LicenseEntry
        title="UMI Gripper"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/umi_gripper",
            label: "mujoco_menagerie/umi_gripper",
          },
        ]}
        licenseText={UMI_LICENSE}
      />

      <LicenseEntry
        title="Boston Dynamics Spot"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/boston_dynamics_spot",
            label: "mujoco_menagerie/boston_dynamics_spot",
          },
        ]}
        licenseText={SPOT_LICENSE}
      />

      <LicenseEntry
        title="Trossen WX250S, Aloha"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/trossen_wx250s",
            label: "mujoco_menagerie/trossen_wx250s",
          },
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/aloha",
            label: "mujoco_menagerie/aloha",
          },
        ]}
        licenseText={TROSSEN_LICENSE}
      />

      <LicenseEntry
        title="Universal Robots UR5e"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/universal_robots_ur5e",
            label: "mujoco_menagerie/universal_robots_ur5e",
          },
        ]}
        licenseText={UR5E_LICENSE}
      />

      <LicenseEntry
        title="Unitree Go2, G1"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/unitree_go2",
            label: "mujoco_menagerie/unitree_go2",
          },
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/unitree_g1",
            label: "mujoco_menagerie/unitree_g1",
          },
        ]}
        licenseText={UNITREE_MENAGERIE_LICENSE}
      />

      <LicenseEntry
        title="Unitree A2, B2, B2-W, Go2-W, H1, H1-2"
        sources={[
          {
            href: "https://github.com/unitreerobotics/unitree_mujoco",
            label: "unitreerobotics/unitree_mujoco",
          },
        ]}
        licenseText={UNITREE_MUJOCO_LICENSE}
      />

      <LicenseEntry
        title="ARX L5"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/arx_l5",
            label: "mujoco_menagerie/arx_l5",
          },
        ]}
        licenseText={ARX_LICENSE}
      />

      <LicenseEntry
        title="Robotiq 2F-85"
        sources={[
          {
            href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/robotiq_2f85",
            label: "mujoco_menagerie/robotiq_2f85",
          },
        ]}
        licenseText={ROBOTIQ_LICENSE}
      />

      <LicenseEntry
        title="Object models (Bottle, Bowl, Cellphone, Plate, Fridge, Desk Cabinet)"
        sources={[
          {
            href: "https://huggingface.co/datasets/allenai/molmospaces",
            label: "allenai/molmospaces (MolmoSpaces THOR subset)",
          },
        ]}
        ccByLicense
      />

      {IS_DEV && (
        <>
          <LicenseEntry
            title="ANYbotics ANYmal B [license under review]"
            sources={[
              {
                href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/anybotics_anymal_b",
                label: "mujoco_menagerie/anybotics_anymal_b",
              },
            ]}
            licenseText={ANYBOTICS_B_LICENSE}
          />

          <LicenseEntry
            title="ANYbotics ANYmal C [license under review]"
            sources={[
              {
                href: "https://github.com/google-deepmind/mujoco_menagerie/tree/main/anybotics_anymal_c",
                label: "mujoco_menagerie/anybotics_anymal_c",
              },
            ]}
            licenseText={ANYBOTICS_C_LICENSE}
          />
        </>
      )}
    </section>
  );
});

const AboutPanel = ({ open, onClose, titleIcon, mode = "about" }) => {
  const isPrivacySettingsMode = mode === "privacy";
  const defaultTabId = ABOUT_TAB_IDS.BROWSER;
  const [activeTabId, setActiveTabId] = React.useState(defaultTabId);

  React.useEffect(() => {
    if (open) setActiveTabId(defaultTabId);
  }, [defaultTabId, open]);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title={isPrivacySettingsMode ? "Privacy Settings" : "About"}
      titleIcon={titleIcon}
      initialSize={{ width: 520, height: 620 }}
      minWidth={320}
      minHeight={360}
    >
      {isPrivacySettingsMode ? null : (
        <AboutTabStrip activeTabId={activeTabId} onTabChange={setActiveTabId} tabs={ABOUT_TABS} />
      )}
      <div
        className="flex-1 overflow-auto border-t border-gray-600/70 p-4 text-sm leading-6 text-gray-200"
        role="tabpanel"
      >
        {isPrivacySettingsMode ? (
          <LocalStorageTabContent />
        ) : activeTabId === ABOUT_TAB_IDS.LICENSES ? (
          <LicensesTabContent />
        ) : activeTabId === ABOUT_TAB_IDS.PRIVACY ? (
          <PrivacyTabContent />
        ) : (
          <BrowserTabContent />
        )}
      </div>
    </DraggablePanel>
  );
};

export default React.memo(AboutPanel);
